import Vue from 'vue'
import Vuex, { ActionTree, GetterTree, MutationTree } from 'vuex'
import OnlineService from '@/services/OnlineService'
import createLogger from 'vuex/dist/logger'
import router from '@/router'
import JWTUtils from '@/utils/JWTUtils'

import { Location } from 'vue-router'
import {
  AuthenticationRequest
} from '@/types/api'

import agreement, { AgreementState } from './modules/agreement'
import registerCode from '@/store/modules/RegisterCode'

Vue.use(Vuex)

/**
 * module を追加した場合は、ここにも追加する
 */
export type RootState = {
  serviceName: string | null;
  auth: AuthState;
  loading: LoadingState;
  agreement?: AgreementState;
  loginError: boolean;
};

export type AuthState = {
  jwt: string | null;
};

export type LoadingState = {
  isLoading: boolean;
  isLoadingForce: boolean;
  loadingIgnore: boolean;
};

const state: RootState = {
  serviceName: process.env.VUE_APP_TITLE,
  auth: {
    jwt: null
  },
  loading: {
    isLoading: false,
    isLoadingForce: false,
    loadingIgnore: false
  },
  loginError: false
}

const mutations: MutationTree<RootState> = {
  auth (state, auth) {
    state.auth = auth
  },
  jwt (state, jwt) {
    state.auth.jwt = jwt
  },
  isLoading (state, isLoading) {
    state.loading.isLoading = isLoading
  },
  isLoadingForce (state, isLoadingForce) {
    state.loading.isLoadingForce = isLoadingForce
    state.loading.isLoading = isLoadingForce
  },
  loadingIgnore (state, loadingIgnore) {
    state.loading.loadingIgnore = loadingIgnore
  },
  loginError (state, loginError) {
    state.loginError = loginError
  }
}

const actions: ActionTree<RootState, RootState> = {
  /**
   * バックエンドで認証エラーの場合の処理
   */
  notAuthorized ({ commit, state, dispatch }): void {
    JWTUtils.logout()
    // JWTが存在する場合はタイムアウト
    const expiration = state.auth.jwt ? JWTUtils.getSubject(state.auth.jwt).expiration : null
    dispatch('clearLogin').then(() => {
      if (expiration) {
        commit('loginError', true)
      }
      router.push('/login')
    })
  },
  /**
   * Auth0認証後の処理
   */
  login ({ commit }, params: AuthenticationRequest): Promise<string | Location | void> {
    return OnlineService.authenticate(params).then(data => {
      const jwtToken = JWTUtils.extract(data)
      JWTUtils.save(jwtToken)
      // axiosのinterceptorで参照するので、jwtTokenは先に登録する
      commit('jwt', JWTUtils.get())
      let result
      // 部店変更が必要の場合は、部店変更確認画面へ遷移する
      if (data.changeDepartmentConfirm) {
        result = '/changeDepartmentConfirm'
      } else {
        // 口座開設申込ステータスにより遷移先を変更する
        const status = data.otcAccountOpeningRequestStatus
        if (status === 'REGISTER_USER') {
          result = '/upload'
        } else if (status === 'UPLOAD_IMAGE_DATA') {
          result = '/registercoderegistration'
        } else if (status === 'REGISTER_ACCOUNT_OPENING') {
          result = '/agreement/contract'
        } else if (status === 'APPLIED_ACCOUNT_OPENING') {
          result = '/complete'
        } else {
          // オンラインで未使用のステータスか、ログイン可能になる前のステータス
          result = '/error/404'
        }
      }
      return Promise.resolve(result)
    }).catch(errors => {
      return Promise.reject(errors)
    })
  },
  /**
   * localStorageに jwtがある場合の自動ログイン
   */
  loginByJwt ({ commit }, { jwtToken }): Promise<string | Location | void> {
    const { companyId, sub: userId } = JWTUtils.getSubject(jwtToken)
    commit('auth', { jwt: jwtToken })
    return OnlineService.status(companyId, userId).then(data => {
      const status = data.otcAccountOpeningRequestStatus
      let result
      // 部店変更が必要の場合は、部店変更確認画面へ遷移する
      if (data.changeDepartmentConfirm) {
        result = '/changeDepartmentConfirm'
      } else {
        if (status === 'REGISTER_USER') {
          result = '/upload'
        } else if (status === 'UPLOAD_IMAGE_DATA') {
          result = '/registercoderegistration'
        } else if (status === 'REGISTER_ACCOUNT_OPENING') {
          result = '/agreement/contract'
        } else if (status === 'APPLIED_ACCOUNT_OPENING') {
          result = '/complete'
        } else {
          // オンラインで未使用のステータスか、ログイン可能になる前のステータス
          result = '/error/404'
        }
      }
      return Promise.resolve(result)
    })
  },
  /**
   * ログイン状態をクリアする
   */
  clearLogin ({ commit, dispatch }): Promise<void[]> {
    JWTUtils.logout()
    commit('auth', { jwt: null })
    const targetModules = ['agreement', 'registerCode']
    return Promise.all(targetModules.map(moduleName => dispatch(`${moduleName}/destroy`)))
  },
  /**
   * ログイン状態のクリア後、画面遷移する（デフォルトでログイン画面）
   */
  logout ({ dispatch }, toPath = '/login') {
    dispatch('clearLogin').then(() => {
      router.push(toPath)
    })
  }
}

const getters: GetterTree<RootState, RootState> = {
  jwt: state => state.auth.jwt,
  isLogined: state => state.auth && state.auth.jwt
}

export default new Vuex.Store<RootState>({
  state,
  mutations,
  actions,
  getters,
  modules: {
    agreement,
    registerCode
  },
  plugins: process.env.NODE_ENV === 'development' ? [createLogger()] : []
})
